import React, {useEffect, useState} from 'react';
import Helmet from 'react-helmet';
import theme from '../design-system/theme';
import SiteHeader from '../components/site-header';
import SiteFooter from '../components/site-footer';
import TrackingConsent from '../components/tracking-consent'
import styled from '@emotion/styled'
import navigation_translation from "../translations/navigation.json";
import SiteDownload from "../components/site-download";
import GlobalStyles from '../design-system/global-styles';

const Layout = ({
                    children,
                    location,
                    title = "",
                    hideDownload = true,
                    hideNavigation = false,
                    noIndex = false,
                    previewImage = "",
                    previewDescription = "",
                    otherLanguagePath = "",
                    richTextJson = "",
                    keywords = ""
                }) => {

    let locationComponents = location.pathname.split("/");
    locationComponents.shift();
    const language = locationComponents[0];

    // store the current location / search in the global theme (dirrrrty)
    theme.windowPathName = location.pathname;
    theme.windowSearch = location.search;
    theme.language = language;

    if (richTextJson.length === 0) {
        richTextJson = JSON.stringify(navigation_translation[language]["rich_text_json"], null, 4);
    }

    if (keywords.length === 0) {
        keywords = navigation_translation[language]["keywords"];
    }

    let hrefLang = (
        ["de", "en"].map((locale) => {
            var pathComponents = location.pathname.split("/");
            if (pathComponents.length > 1) {

                if (locale !== pathComponents[1] && otherLanguagePath.length > 0) {
                    return <link rel="alternate" href={otherLanguagePath} hrefLang={locale}/>;
                } else {
                    pathComponents[1] = locale;
                    const href = 'https://www.tyme-app.com' + pathComponents.join("/");
                    return <link rel="alternate" href={href} hrefLang={locale}/>;
                }
            }

            return "";
        })
    )

    let canonicalURL = 'https://www.tyme-app.com' + location.pathname;

    let ogDescription = previewDescription === "" ? navigation_translation[language]["description"] : previewDescription;
    let ogImage = previewImage === "" ? "https://www.tyme-app.com/images/og-image.jpg" : previewImage;

    if (!ogImage.includes("https")) {
        if (ogImage.charAt(0) === "/") {
            ogImage = ogImage.substr(1);
        }
        ogImage = "https://www.tyme-app.com/" + ogImage;
    }

    let htmlTitle = "Tyme: " + title;

    let hideNavigationFromURL = location.search.includes("hide_nav");
    let reallyHideNavigation = hideNavigation || hideNavigationFromURL;

    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const isiOSDevice = navigator.userAgent.includes("iPhone")
                || navigator.userAgent.includes("iPad")
                || navigator.userAgent.includes("iPod");
            setIsIos(isiOSDevice);
        }
    }, []);

    return (
        <StyledSection>
            <GlobalStyles/>
            <Helmet
                title={htmlTitle}
                meta={[
                    {
                        name: 'publisher',
                        content: 'Unit Numberfive oHG'
                    },
                    {
                        name: 'copyright',
                        content: 'Unit Numberfive oHG'
                    },
                    {
                        name: 'author',
                        content: 'Lars Gerckens'
                    },
                    {
                        name: 'page-type',
                        content: 'Software Download'
                    },
                    {
                        name: 'page-topic',
                        content: navigation_translation[language]["page_topic"]
                    },
                    {
                        name: 'description',
                        content: ogDescription
                    },
                    {
                        'http-equiv': 'content-language',
                        content: language
                    },
                    {
                        name: 'robots',
                        content: noIndex ? 'noindex' : 'index, follow'
                    },
                    {
                        name: 'keywords',
                        content: keywords
                    },
                    {
                        property: 'og:title',
                        content: title
                    },
                    {
                        property: 'og:description',
                        content: ogDescription
                    },
                    {
                        property: 'og:image',
                        content: ogImage
                    },
                    {
                        property: 'og:type',
                        content: 'article'
                    },
                    {
                        property: 'og:url',
                        content: 'https://www.tyme-app.com' + location.pathname
                    },
                    {
                        name: 'twitter:card',
                        content: 'summary'
                    },
                    {
                        name: 'twitter:title',
                        content: title
                    },
                    {
                        name: 'twitter:url',
                        content: 'https://www.tyme-app.com' + location.pathname
                    },
                    {
                        name: 'twitter:description',
                        content: ogDescription
                    },
                    {
                        name: 'twitter:image',
                        content: ogImage
                    }
                ]}
            >
                {hrefLang}
                <link rel="canonical" href={canonicalURL}/>
                <script type='application/ld+json'>
                    {
                        /*
                        https://search.google.com/test/rich-results
                        */

                        `{
                        "@context": "http://www.schema.org",
                        "@type": "WebSite",
                        "name": "Tyme",
                        "headline": "${title}",
                        "image": "${ogImage}",
                        "url": "https://www.tyme-app.com"
                    }`}
                </script>
                <script type="application/ld+json">
                    {`{
                        "@context": "http://schema.org",
                        "@type": "Organization",
                        "url": "https://www.tyme-app.com",
                        "name": "Tyme",
                        "inLanguage": "${language}",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "url": "https://www.tyme-app.com/${language}/contact/",
                            "contactType": "customer support"
                        }
                    }`}
                </script>
                <script type="application/ld+json">
                    {richTextJson}
                </script>
            </Helmet>
            {reallyHideNavigation ? null :
                <SiteHeader location={location} textResources={navigation_translation[language]}/>}
            {children}
            {(reallyHideNavigation || hideDownload) ? null :
                <SiteDownload
                    color="rgb(0, 202, 143)"
                    isIos={isIos}
                    textResources={navigation_translation[language]["site_download"]}
                />}
            {reallyHideNavigation ? null :
                <SiteFooter location={location} textResources={navigation_translation[language]}/>}
            {reallyHideNavigation ? null :
                <TrackingConsent location={location} textResources={navigation_translation[language]}/>}
        </StyledSection>
    );
}

const StyledSection = styled('section')`
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 100%;
`;

export default Layout;
